import { COMMAND_TYPES } from '@integrate/contracts';
/**
 * A short human name per command type.
 *
 * Held to the catalogue by `MissingFromLabels` below, which is the same compile-time trick
 * `history.ts` uses on `PRECEDENCE`: a new command type is a build error naming the type, not a diff row
 * reading `setBucketBulk` at somebody who is deciding whether to approve it.
 */
export const COMMAND_LABELS = {
    setBucket: 'Move to a bucket',
    setBucketBulk: 'Move several people to a bucket',
    setJoinStage: 'Set the joining stage',
    setDay2Exit: 'Flag a Day 2 exit',
    setStrandedPlan: 'Set a stranded mitigation plan',
    applyPerimeterThreshold: 'Apply the perimeter rule',
    clearPerimeterThreshold: 'Clear the perimeter rule',
    setPerimeterRule: 'Store the perimeter rule without applying it',
    setPerimeterFrozen: 'Freeze or unfreeze the perimeter',
    setHead: 'Designate a head',
    clearHead: 'Remove a head designation',
    renameGroup: 'Rename a group',
    moveGroup: 'Move a group',
    setLevelDisabled: 'Turn a hierarchy level off',
    setEmpField: 'Override a field on one person',
    setEmpFieldBulk: 'Override a field on several people',
    resetRestructuring: 'Reset this perspective’s restructuring',
    addBuildUp: 'Add a planned position',
    updateBuildUp: 'Change a planned position',
    deleteBuildUp: 'Delete a planned position',
    addTsa: 'Add a service agreement',
    updateTsa: 'Change a service agreement',
    deleteTsa: 'Delete a service agreement',
    setTargetEnabled: 'Turn the target organisation on or off',
    setTargetLevels: 'Change the target ladder',
    addTargetUnit: 'Add a target unit',
    declareTargetUnits: 'Declare a set of target units',
    removeTargetUnit: 'Remove a target unit',
    renameTargetValue: 'Rename a target value',
    reparentTargetUnit: 'Move a target unit',
    setTargetPlacement: 'Place somebody in the target organisation',
    createScenario: 'Create a scenario',
    duplicateScenario: 'Duplicate a scenario',
    renameScenario: 'Rename a scenario',
    deleteScenario: 'Delete a scenario',
    setDefaultScenario: 'Set the scenario a project opens on',
    addEntity: 'Add a legal entity',
    updateEntity: 'Change a legal entity',
    deleteEntity: 'Delete a legal entity',
    remapEntity: 'Remap a legal entity',
    setAssumption: 'Change a cost assumption',
    setColumnMapping: 'Change the column mapping',
    setGroupingLevels: 'Change the grouping levels',
    applyDataUpdate: 'Refresh the roster from a new file',
    rebuildRosterFromSource: 'Re-derive the roster from the source rows',
    saveView: 'Save a view',
    updateView: 'Change a saved view',
    deleteView: 'Delete a saved view',
    setDefaultView: 'Set the default view',
    setNodeDisplay: 'Change what the cards show',
    setPptxGeom: 'Change the deck geometry',
    renameProject: 'Rename the project',
    archiveProject: 'Archive or restore the project',
    addNote: 'Add a note',
    annotateEvent: 'Note against an earlier change',
    restoreProject: 'Restore the project to a point in time',
    issueSteercoPack: 'Issue a SteerCo pack',
};
const _everyCommandIsLabelled = true;
void _everyCommandIsLabelled;
/** Field names whose value is a person id, so the roster can answer for them. */
const PERSON_ID_KEYS = new Set(['employeeId', 'buildUpId', 'headEmployeeId']);
/**
 * Render one value.
 *
 * Arrays and objects are summarised by SIZE rather than dumped. A `setBucketBulk` carrying four hundred
 * pairs is a real proposal and a real thing to approve, and pasting four hundred rows into a modal is not
 * showing it to somebody — it is burying it. The count is the honest headline; the detail is the payload,
 * which the reader can already fetch.
 */
function renderValue(key, value, nameOf) {
    if (value === null)
        return '—';
    if (value === undefined)
        return '—';
    if (Array.isArray(value))
        return `${value.length} ${value.length === 1 ? 'entry' : 'entries'}`;
    if (typeof value === 'object')
        return `${Object.keys(value).length} field(s)`;
    if (typeof value === 'boolean')
        return value ? 'yes' : 'no';
    const text = String(value);
    if (PERSON_ID_KEYS.has(key))
        return nameOf(text) ?? text;
    return text;
}
/**
 * Fields the reader does not need and the row is better without.
 *
 * `scenarioId` is on nearly every command and is the same for the whole proposal, which the header already
 * says; repeating an opaque id on every line is noise that makes the lines that matter harder to find.
 */
const HIDDEN_FIELDS = new Set(['type', 'scenarioId']);
export function describeCommand(command, nameOf = () => undefined) {
    const type = command.type;
    return {
        type,
        label: COMMAND_LABELS[type],
        fields: Object.entries(command)
            .filter(([key]) => !HIDDEN_FIELDS.has(key))
            .map(([key, value]) => ({ key, value: renderValue(key, value, nameOf) })),
    };
}
/** Every command type the catalogue holds, for a test that wants to prove the labels cover it. */
export const LABELLED_COMMAND_TYPES = COMMAND_TYPES;
/* ------------------------------------------------------------------ *
 * A batch, read as a whole
 * ------------------------------------------------------------------ */
/**
 * The same rendering, but with the one thing a per-command view cannot know: what the OTHER commands
 * in the proposal say about this one.
 *
 * WHY THIS EXISTS, and it is a defect report rather than a nicety. `addBuildUp` carries no bucket —
 * a bucket is a decision, held in `design.buckets` against an id, exactly as it is for a real
 * employee. So a planned hire meant for the carve-out takes TWO commands, which the command's own
 * description says: "It starts in Remain; setBucket is what puts it in the carve-out."
 *
 * An assistant that emitted only the first produced a proposal that validated cleanly, applied
 * cleanly, and put the hire in RemainCo. And the diff a reviewer read said:
 *
 *     Add a planned position     buildUp: 12 field(s)
 *
 * — which names neither the person nor the side they land on, so the omission was invisible at every
 * layer. Rendering the OBJECT by its size is right for a `setBucketBulk` carrying four hundred pairs
 * and wrong for the one object whose contents are the entire content of the row.
 *
 * So a batch resolves what a single command cannot:
 *
 *  · a planned hire is named and roled, rather than counted;
 *  · every added position states THE BUCKET IT LANDS IN, found from the sibling commands, and says so
 *    explicitly when nothing set one — which is the wrong-looking row that makes the bug visible;
 *  · an id that belongs to a hire created in the same batch resolves to their name, which no roster
 *    lookup could do because they do not exist yet.
 *
 * It still renders THE COMMANDS and never predicts an outcome — "lands in" is read off a sibling
 * command in the same atomic batch, not computed against state the handlers own.
 */
export function describeBatch(commands, nameOf = () => undefined) {
    /*
      Names for people this batch is about to create. A `setBucket` naming a hire added two rows above it
      would otherwise render a raw id — the reader would see the bucket move and not know whose.
    */
    const pending = new Map();
    for (const command of commands) {
        if (command.type === 'addBuildUp')
            pending.set(command.buildUp.id, command.buildUp.name);
    }
    const resolve = (id) => nameOf(id) ?? pending.get(id);
    /*
      The LAST word on each id wins, because that is what applying the batch in order would do. A
      proposal that moves somebody twice is unusual and legitimate, and reporting the first move would be
      reporting a state the reader never reaches.
    */
    const bucketOf = new Map();
    for (const command of commands) {
        if (command.type === 'setBucket') {
            if (command.bucket !== null)
                bucketOf.set(command.employeeId, command.bucket);
            else
                bucketOf.delete(command.employeeId);
        }
        else if (command.type === 'setBucketBulk') {
            for (const a of command.assignments) {
                if (a.bucket !== null)
                    bucketOf.set(a.employeeId, a.bucket);
                else
                    bucketOf.delete(a.employeeId);
            }
        }
    }
    return commands.map((command) => {
        const described = describeCommand(command, resolve);
        if (command.type !== 'addBuildUp')
            return described;
        const hire = command.buildUp;
        const landsIn = bucketOf.get(hire.id);
        return {
            ...described,
            fields: [
                { key: 'name', value: hire.name },
                ...(hire.jobTitle ? [{ key: 'role', value: hire.jobTitle }] : []),
                { key: 'wave', value: String(hire.wave) },
                /*
                  THE ROW THIS FUNCTION WAS WRITTEN FOR. Said the same way whether or not a bucket was set, so
                  a correct RemainCo hire and a carve-out hire that lost its second command look different on
                  screen instead of both reading as an opaque field count.
                */
                {
                    key: 'lands in',
                    value: landsIn ?? 'Remain — no bucket set in this proposal',
                },
                // Everything else the command carried, minus the object this row just unpacked.
                ...described.fields.filter((f) => f.key !== 'buildUp'),
            ],
        };
    });
}
//# sourceMappingURL=describe.js.map